import { Box } from '@react-three/drei';
import { bridge, useUnity } from '@sloyd/viewer';
import React, { useEffect, useRef, useState } from 'react';
import { Unity } from 'react-unity-webgl';
import { useMount } from 'react-use';
import { downloadJson } from 'shared-base';
import { useModel } from '../../extra/extra.hooks';
import { cameraMove, getModel, init } from '../../extra/extra.utils';
import Card from '../Card/Card';
import ModelSpin from '../ModelSpin/ModelSpin';
import { useEditorBackground } from './UnityRecord.hooks';
import { Actions, Ready, Wrapper } from './UnityRecorder.style';
import { getFileName, numberToGeneratorId } from './UnityRecorder.utils';

const environment: IHolodeckState = {
  areGridLinesVisible: true,
  gridLinesColor: '#96C5FA',
  gridLinesOpacity: 0.75,
  lightColor: '#FFFDED',
  lightIntensity: 3,
  isEditorDarkMode: true,
  backgroundColor: '#1c2b5a',
  backgroundIntensity: 0.3,
  backdropGrainIntensity: 0.08,
  backdropEnabled: false,
  backdropId: '',
  cameraLock: false,
};

export const UnityRecorder: React.FC = (props: any) => {
  const { unityProvider, isLoaded } = useUnity();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const [recording, setRecording] = useState(false);
  const [generatorId, setGeneratorId] = useState(props.generatorIndex);
  const [iterationId, setIterationId] = useState(1);
  const [isReady, { random }] = useModel(numberToGeneratorId(generatorId), isLoaded);

  const filename = getFileName(generatorId, iterationId);

  useMount(() => {
    init();
  });

  useEffect(() => {
    setIterationId(1);
  }, [generatorId]);

  const style = useEditorBackground({ ...environment });

  useEffect(() => {
    if (!isLoaded) return;
    bridge.setEnvironment('main', environment);
  }, [isLoaded]);

  const downloadImage = () => {
    const canvas = document.querySelector('canvas') as HTMLCanvasElement;
    if (!canvas) return;

    // Convert canvas content to a data URL (image)
    const imageURL = canvas.toDataURL('image/png');

    // Create a link element to trigger download
    const downloadLink = document.createElement('a');
    downloadLink.href = imageURL;
    downloadLink.download = filename + '.png'; // Set file name
    downloadLink.click();
    setIterationId((prev) => prev + 1);
  };

  const getJson = () => {
    const model = getModel();
    downloadJson(filename + '.json', model);
  };

  const startRecording = () => {
    const canvas = document.querySelector('.Canvas-wrapper canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const stream = canvas.captureStream(30); // Capture at 30 FPS

    mediaRecorderRef.current = new MediaRecorder(stream, {
      mimeType: 'video/webm; codecs=vp9',
    });

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      if (videoRef.current) {
        videoRef.current.src = url;
      }
    };

    mediaRecorderRef.current.start();

    setTimeout(() => {
      cameraMove();
    }, 1000);

    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  function animate() {
    cameraMove();
  }

  function renderAnimation() {
    return (
      <>
        <Box>
          <ModelSpin />
          <div>Press 'a' to switch models</div>
        </Box>
        <Box>
          <Card level='gold' />
        </Box>
      </>
    );
  }

  return (
    <Wrapper>
      {isReady && <Ready data-testid='ready' />}
      <Box>
        <Actions>
          <label>G-</label>
          <input
            value={generatorId}
            type='number'
            data-testid='input-generator'
            onChange={(e) => setGeneratorId(e.target.value)}
          />
          <button data-testid='btn-random' onClick={() => random(true)}>
            Random
          </button>
          <textarea className='code' data-testid='code' style={{ width: '1px', height: '2px' }} />
          {/* <button onClick={() => random(true)}>Random & focus</button> */}
          <button data-testid='btn-animate' onClick={animate}>
            Animate
          </button>
        </Actions>
        <Unity
          unityProvider={unityProvider}
          style={{ width: '1000px', height: '800px', ...style }}
        />
      </Box>
      <Box>
        <Actions>
          <button
            onClick={() => {
              downloadImage();
              getJson();
            }}
          >
            Download
          </button>

          <input
            value={iterationId}
            onChange={(ev: any) => setIterationId(ev.target.value)}
            type='number'
            data-testid='input-iteration'
          />
          <button data-testid='btn-downloadImage' onClick={downloadImage}>
            Img
          </button>
          <button data-testid='btn-downloadJson' onClick={getJson}>
            Json
          </button>
          <button
            data-testid='btn-startRecording'
            onClick={recording ? stopRecording : startRecording}
          >
            {recording ? 'Stop Recording' : 'Start Recording'}
          </button>
        </Actions>
        <video ref={videoRef} controls style={{ marginTop: '10px', width: '500px' }} />
        {/* {renderAnimation()} */}
      </Box>
    </Wrapper>
  );
};

export default UnityRecorder;
